import { ShoppingBag, Store, Truck, Wallet } from "lucide-react";
import { Pill, type PillVariant } from "@veorest/ui";
import { formatCurrency, formatOrderDisplayNumber } from "@veorest/utils";
import { StatsStrip, type StatTileItem } from "../../data/StatsStrip";
import { DailyBars, DailyBarsCaption } from "../../data/DailyBars";
import {
  partnerStageTranslationKey,
  type PartnerOrderStage,
} from "../../../lib/partnerOrders";
import type {
  PartnerClientOverviewRow,
  PartnerClientReport,
} from "../../../lib/partnerReports";import { usePartnerClientOrders } from "../../../hooks/usePartnerReports";
import { usePartnerClientLedger } from "../../../hooks/usePartnerWallets";
import type { PartnerDashboardDateRange } from "../../../lib/partnerDashboardDate";
import { useI18n } from "../../../i18n";

interface ClientOverviewTabProps {
  client: PartnerClientOverviewRow;
  report: PartnerClientReport | undefined;
  loading: boolean;
  range: PartnerDashboardDateRange;
}

const STAGE_PILL: Record<PartnerOrderStage, PillVariant> = {
  pending: "warning",
  processing: "info",
  ready: "accent",
  in_delivery: "info",
  completed: "success",
  cancelled: "danger",
};

function formatTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

export function ClientOverviewTab({
  client,
  report,
  loading,
  range,
}: ClientOverviewTabProps) {
  const { t, locale } = useI18n();
  const currency = client.currency || "IQD";
  const money = (v: number) => formatCurrency(v, currency, locale);

  const recentQuery = usePartnerClientOrders(client.id, range, {
    page: 0,
    pageSize: 8,
  });
  const ledgerQuery = usePartnerClientLedger(client.id);
  const dues = ledgerQuery.data?.totals;
  const recent = recentQuery.data?.orders ?? [];
  const kpis = report?.kpis;
  const series = report?.series ?? [];
  const firstDay = series.length > 0 ? series[0]?.day : undefined;
  const lastDay = series.length > 0 ? series[series.length - 1]?.day : undefined;

  const tiles: StatTileItem[] = [
    {
      key: "orders",
      label: t("clientView.ordersRange"),
      value: kpis?.orders_total ?? "—",
      icon: ShoppingBag,
      tone: "accent",
      caption:
        kpis != null
          ? `${kpis.orders_delivery} ${t("common.delivery")} · ${kpis.orders_pickup} ${t("common.pickup")} · ${kpis.orders_cancelled} ${t("orders.stages.cancelled")}`
          : undefined,
    },
    {
      key: "income",
      label: t("clientView.deliveryIncome"),
      value: kpis ? money(kpis.delivery_income) : "—",
      icon: Truck,
      tone: "accent",
      caption: kpis
        ? `${t("analytics.fromCustomers")} ${money(kpis.delivery_income_customer)} · ${t("analytics.fromClients")} ${money(kpis.delivery_income_client)}`
        : undefined,
    },
    {
      key: "dues",
      label: t("clientLedger.balanceToPay"),
      value: dues ? money(dues.balance_to_pay) : "—",
      icon: Wallet,
      tone: dues && dues.balance_to_pay > 0 ? "info" : "success",
      caption: dues
        ? `${t("clientLedger.feesTotal")} ${money(dues.fees_total)} · ${t("clientLedger.paidTotal")} ${money(dues.paid_total)}`
        : undefined,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <StatsStrip tiles={tiles} loading={loading} />

      <p className="text-xs text-[var(--text-muted)]">
        {kpis?.orders_completed ?? 0} {t("orders.stages.completed")} ·{" "}
        {client.active_orders_count} {t("clientView.activeNow")} ·{" "}
        {client.active_drivers_count} {t("nav.drivers")}
      </p>

      {kpis && kpis.legacy_unpriced_count > 0 ? (
        <p className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text-muted)]">
          {t("clientView.legacyNote", { count: kpis.legacy_unpriced_count })}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="menu-panel menu-panel--chart">
          <div className="menu-panel__body">
            <h3 className="mb-2 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("analytics.ordersPerDay")}
            </h3>
            <DailyBars
              points={series.map((p) => ({ day: p.day, value: p.orders }))}
              ariaLabel={t("analytics.ordersPerDay")}
            />
            {firstDay && lastDay ? (
              <DailyBarsCaption first={firstDay} last={lastDay} />
            ) : null}
          </div>
        </section>
        <section className="menu-panel menu-panel--chart">
          <div className="menu-panel__body">
            <h3 className="mb-2 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("analytics.incomePerDay")}
            </h3>
            <DailyBars
              points={series.map((p) => ({ day: p.day, value: p.delivery_income }))}
              ariaLabel={t("analytics.incomePerDay")}
              tone="info"
              formatValue={money}
            />
            {firstDay && lastDay ? (
              <DailyBarsCaption first={firstDay} last={lastDay} />
            ) : null}
          </div>
        </section>
      </div>

      <section className="menu-panel">
        <div className="menu-panel__body">
          <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("overview.recentOrders.title")}
          </h3>
          {recentQuery.isLoading ? (
            <div className="h-20 rounded-xl skeleton" />
          ) : recent.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">{t("clientView.noOrdersRange")}</p>
          ) : (
            <ul className="overview-list">
              {recent.map((order) => (
                <li key={order.id}>
                  <div className="overview-list-row">
                    <div
                      className="overview-list-row__avatar"
                      style={{
                        background:
                          "color-mix(in srgb, var(--accent) 14%, transparent)",
                        color: "var(--accent)",
                      }}
                      aria-hidden
                    >
                      <Store className="h-4 w-4" strokeWidth={2.25} />
                    </div>
                    <div className="overview-list-row__main min-w-0">
                      <p className="overview-list-row__title">
                        {formatOrderDisplayNumber(order.id)}
                        <span> · {order.customer_name}</span>
                      </p>
                      <p className="overview-list-row__meta">
                        {order.driver_name ?? t("common.dash")}
                        {" · "}
                        {order.fulfillment_type === "pickup"
                          ? t("common.pickup")
                          : t("common.delivery")}
                        {" · "}
                        {money(order.total)}
                      </p>
                    </div>
                    <div className="overview-list-row__end">
                      <Pill variant={STAGE_PILL[order.stage]}>
                        {t(partnerStageTranslationKey(order.stage))}
                      </Pill>
                      <span className="overview-list-row__time">
                        {formatTime(order.created_at)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
